/**
 * Перенос EXIF из оригинала в пережатый снимок.
 *
 * Правило 3 требует сохранять EXIF, но canvas при перекодировании его стирает:
 * получается снимок без времени съёмки, модели камеры и ориентации. Поэтому
 * сегмент APP1 вырезается из оригинала и вставляется в результат вручную.
 *
 * Формат JPEG: SOI (FFD8), затем сегменты FFxx + 2 байта длины
 * (big-endian, включая сами байты длины). EXIF живёт в APP1 (FFE1)
 * с сигнатурой "Exif" и двумя нулевыми байтами.
 */

const MARKER = 0xff;
const SOI = 0xd8;
const APP1 = 0xe1;
const SOS = 0xda;

/** Байты сигнатуры: E x i f NUL NUL. Сравниваем численно, без строк. */
const EXIF_SIGNATURE = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00] as const;

function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length > 3 && bytes[0] === MARKER && bytes[1] === SOI;
}

function hasExifSignature(bytes: Uint8Array, at: number): boolean {
  return EXIF_SIGNATURE.every((byte, i) => bytes[at + i] === byte);
}

/** Смещение и длина сегмента APP1 с EXIF, либо null. */
export function findExifSegment(bytes: Uint8Array): { start: number; length: number } | null {
  if (!isJpeg(bytes)) return null;

  let offset = 2;
  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== MARKER) return null;

    const marker = bytes[offset + 1];
    // После SOS идут сжатые данные — сегментов там уже нет.
    if (marker === undefined || marker === SOS) return null;

    const high = bytes[offset + 2];
    const low = bytes[offset + 3];
    if (high === undefined || low === undefined) return null;

    const length = (high << 8) | low;
    if (length < 2 || offset + 2 + length > bytes.length) return null;

    if (marker === APP1 && hasExifSignature(bytes, offset + 4)) {
      return { start: offset, length: 2 + length };
    }

    offset += 2 + length;
  }

  return null;
}

export function extractExifSegment(bytes: Uint8Array): Uint8Array | null {
  const found = findExifSegment(bytes);
  return found === null ? null : bytes.slice(found.start, found.start + found.length);
}

/**
 * Вставляет сегмент EXIF сразу после SOI.
 * Существующий APP1 в цели удаляется: два EXIF-сегмента в одном файле —
 * некорректный JPEG, и парсеры читают из них разное.
 */
export function injectExifSegment(target: Uint8Array, exif: Uint8Array): Uint8Array {
  if (!isJpeg(target)) return target;

  const existing = findExifSegment(target);
  const cleaned =
    existing === null
      ? target
      : (() => {
          const out = new Uint8Array(target.length - existing.length);
          out.set(target.subarray(0, existing.start), 0);
          out.set(target.subarray(existing.start + existing.length), existing.start);
          return out;
        })();

  const result = new Uint8Array(cleaned.length + exif.length);
  result.set(cleaned.subarray(0, 2), 0);
  result.set(exif, 2);
  result.set(cleaned.subarray(2), 2 + exif.length);
  return result;
}

/** Перенос EXIF из оригинала в пережатый снимок. Нет EXIF — возвращаем как есть. */
export function transplantExif(original: Uint8Array, resized: Uint8Array): Uint8Array {
  const exif = extractExifSegment(original);
  return exif === null ? resized : injectExifSegment(resized, exif);
}
